import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
  color: ${props => props.theme.foreground};
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  position: relative;
`;

const VersionBadge = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  font-size: 0.75rem;
  color: ${props => props.theme.mutedForeground};
  font-weight: 400;
  
  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: ${props => props.theme.foreground};
`;

const LastUpdated = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.mutedForeground};
  margin: 0;
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 1rem;
  color: ${props => props.theme.primary};
  text-decoration: none;
  font-size: 0.875rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const NextLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-top: 3rem;
  padding: 0.75rem 1.5rem;
  color: ${props => props.theme.primary};
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid ${props => props.theme.border};
  border-radius: ${props => props.theme.radius};
  background: ${props => props.theme.card};
  transition: all 0.2s;
  margin-left: auto;
  
  &:hover {
    background: ${props => props.theme.accent};
    border-color: ${props => props.theme.primary};
  }
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid ${props => props.theme.border};
`;

const AcceptedMessage = styled.div`
  margin-top: 3rem;
  padding: 1rem;
  background: ${props => props.theme.muted};
  border: 1px solid ${props => props.theme.border};
  border-radius: ${props => props.theme.radius};
  color: ${props => props.theme.foreground};
  font-size: 0.875rem;
  text-align: center;
`;

const Content = styled.div`
  font-size: 1rem;
  color: ${props => props.theme.foreground};
  
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 2rem 0 1rem 0;
    color: ${props => props.theme.foreground};
  }
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 1.5rem 0 0.75rem 0;
    color: ${props => props.theme.foreground};
  }
  
  p {
    margin: 1rem 0;
    color: ${props => props.theme.foreground};
  }
  
  ul, ol {
    margin: 1rem 0;
    padding-left: 2rem;
    color: ${props => props.theme.foreground};
  }
  
  li {
    margin: 0.5rem 0;
  }
`;

const TermsOfUse: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isSignup = searchParams.get('signup') === 'true';
  const fromLogin = searchParams.get('from') === 'login';
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean | null>(null);
  const [checkingTerms, setCheckingTerms] = useState(false);
  const { isAuthenticated, getAuthToken } = useAuth();

  useEffect(() => {
    // If not a signup flow, clear any pending signup data
    if (!isSignup) {
      sessionStorage.removeItem('pendingSignup');
    }
  }, [isSignup]);

  useEffect(() => {
    // Check if user has already accepted terms (only if authenticated and not in signup flow)
    const checkTermsStatus = async () => {
      if (isAuthenticated && !isSignup) {
        setCheckingTerms(true);
        try {
          const token = await getAuthToken();
          if (!token) {
            setCheckingTerms(false);
            return;
          }

          const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
          const response = await fetch(`${apiUrl}/auth/terms-status`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            setHasAcceptedTerms(data.hasAccepted);
          }
        } catch (error) {
          console.error('Error checking terms status:', error);
        } finally {
          setCheckingTerms(false);
        }
      }
    };

    checkTermsStatus();
  }, [isAuthenticated, isSignup, getAuthToken]);

  return (
    <Container>
      <VersionBadge>v1.0</VersionBadge>
      
      {!isSignup && (
        <BackLink to={fromLogin ? "/login" : "/signup"}>
          ← {fromLogin ? "Back to Sign In" : "Back to Sign Up"}
        </BackLink>
      )}
      
      <Header>
        <Title>Terms of Use</Title>
        <LastUpdated>Last Updated: 05/01/2026</LastUpdated>
      </Header>

      <Content>
        <p>
          These Terms of Use ("Terms") govern access to and use of TenantPlus, a property management and tenant services platform operated by Circle Plus Technologies ("Circle Plus", "we", "us", "our"). By creating an account or using Tenant Plus, you agree to these Terms.
        </p>

        <h2>1. The Platform</h2>
        <p>
          TenantPlus pro vides software tools for property owners and managers to manage properties and tenants, and separate services and tools for tenants. We provide technology only and are not a party to any lease, rental, or property agreement.
        </p>

        <h2>2. User Types</h2>
        <p>
          The Platform supports two types of users:
        </p>
        <ul>
          <li><strong>Property Owners / Managers ("Landlords")</strong> – account holders who manage properties and invite tenants.</li>
          <li><strong>Tenants ("Tenants")</strong> – end users who access the Platform through a tenant portal or direct account.</li>
        </ul>
        <p>
          Each user type has different rights and responsibilities.
        </p>

        <h2>3. Landlord Accounts</h2>
        <p>
          Landlords are responsible for:
        </p>
        <ul>
          <li>The accuracy of property, rent, and tenant information they provide</li>
          <li>Managing tenant access to the Platform</li>
          <li>Compliance with applicable housing, tenancy, and data protection laws</li>
        </ul>
        <p>
          Landlords acknowledge and agree that tenants may have a direct and independent relationship with the Platform as described in Section 5.
        </p>

        <h2>4. Tenant Accounts & Portal Access</h2>
        <p>
          Tenant access is provided through a tenant portal or tenant account and may be granted, limited, or revoked by the landlord at any time.
        </p>
        <p>
          Tenants may:
        </p>
        <ul>
          <li>View their own payment information</li>
          <li>Submit maintenance or service requests</li>
          <li>Communicate with landlords through the Platform</li>
        </ul>
        <p>
          We do not guarantee the accuracy of data provided by landlords or the resolution of tenant requests.
        </p>

        <h2>5. Tenant Services & Independent Relationship</h2>
        <p>
          By using TenantPlus, landlords agree that the Platform may offer services directly to tenants independently of the landlord.
        </p>
        <p>
          Landlords acknowledge and agree that:
        </p>
        <ul>
          <li>The Platform may offer services directly to tenants</li>
          <li>Such services do not make the Platform an agent or representative of the landlord</li>
          <li>Tenant services do not replace, modify, or enforce lease agreements</li>
        </ul>
        <p>
          Tenants acknowledge that tenant services are separate from landlord obligations and do not create rights against landlords.
        </p>

        <h2>6. No Involvement in Landlord–Tenant Relationships</h2>
        <p>
          We are not a party to any lease, rental agreement, or dispute between landlords and tenants. We do not:
        </p>
        <ul>
          <li>Collect rent as a trustee</li>
          <li>Enforce lease terms</li>
          <li>Resolve disputes</li>
          <li>Guarantee payments or outcomes</li>
        </ul>
        <p>
          All legal and contractual obligations remain solely between landlords and tenants.
        </p>

        <h2>7. Messaging & Communications</h2>
        <p>
          By using the Platform, users consent to receive service-related communications via email, SMS, WhatsApp, or similar channels. Message frequency may vary, and users may manage communication preferences where available.
        </p>

        <h2>8. Payments & Fees</h2>
        <p>
          Certain features or services may require payment. Fees, billing terms, and subscription details will be disclosed before purchase. Unless stated otherwise, payments are non-refundable.
        </p>

        <h2>9. Acceptable Use</h2>
        <p>
          Users may not:
        </p>
        <ul>
          <li>Misuse the Platform</li>
          <li>Access data they are not authorized to view</li>
          <li>Interfere with system performance</li>
          <li>Use the Platform for unlawful purposes</li>
        </ul>
        <p>
          We reserve the right to suspend or terminate accounts for violations.
        </p>

        <h2>10. Intellectual Property</h2>
        <p>
          The Platform, software, and all related content are owned by Circle Plus Technologies operate under the TenantPlus brand. Users are granted a limited, non-transferable right to use the Platform as intended.
        </p>

        <h2>11. Disclaimers</h2>
        <p>
          The Platform is provided "as is" without warranties of any kind. We do not guarantee uninterrupted service, data accuracy, or specific results.
        </p>

        <h2>12. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, TenantPlus shall not be liable for indirect, incidental, or consequential damages. Our total liability shall not exceed the amount paid by you to us in the previous 12 months.
        </p>

        <h2>13. Termination</h2>
        <p>
          Users may stop using the Platform at any time. We may suspend or terminate access for misuse, legal requirements, or operational reasons.
        </p>

        <h2>14. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. Continued use of the Platform after changes constitutes acceptance.
        </p>

        <h2>15. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the Republic of Kenya.
        </p>
      </Content>

      {isSignup && (
        <FooterActions>
          <NextLink to={`/privacy?signup=true${fromLogin ? '&from=login' : ''}`}>
            Next: Privacy Policy →
          </NextLink>
        </FooterActions>
      )}

      {!isSignup && isAuthenticated && hasAcceptedTerms === true && (
        <AcceptedMessage>
          ✓ You have already accepted the Terms of Use and Privacy Policy.
        </AcceptedMessage>
      )}
    </Container>
  );
};

export default TermsOfUse;

